import {GrowthBook} from '@growthbook/growthbook';
import {isMobile} from './util/is-mobile';
import {getMParticleEventAttributes} from './integrations/mparticle';
import type {Context, TrackingData} from '@growthbook/growthbook';

import type {GrowthBookAttributes, TreatwellAttributes} from './types';
import type {MParticleIntegration} from './integrations/mparticle';
import {log} from './util/log';
import {isDebugEnabled} from './util/is-debug';

export {getDeviceId} from './util/device-id';

export type {GrowthBookAttributes, TreatwellAttributes};

export type Options = {
  /**
   * Provide attributes to be set on the GrowthBook client.
   * These are defined in the GrowthBook UI.
   */
  growthbookAttributes?: GrowthBookAttributes;

  /**
   * Provide an integration to send activation events to a third-party service.
   * Currently only mParticle is supported.
   */
  activationEvent?: MParticleIntegration;

  /**
   * Allow streaming of updates directly with SSE
   * https://docs.growthbook.io/lib/js#streaming-updates
   */
  enableStreaming?: boolean;

  /**
   * Calls already generated from the server side
   * https://docs.growthbook.io/lib/node#deferred-tracking
   */
  deferredTrackingCalls?: TrackingData[];
};

export async function growthbookInitialiseClient(
  growthbookContext: Context,
  deviceId: string,
  {
    growthbookAttributes = {},
    activationEvent,
    enableStreaming,
    deferredTrackingCalls,
  }: Options = {},
) {
  const isMobileDevice = isMobile(navigator.userAgent);
  const attributes: TreatwellAttributes = {
    ...growthbookAttributes.channel,
    ...growthbookAttributes.user,
    growthbook_id: deviceId,
    platform: 'web',
    is_web_mobile: isMobileDevice,
  };

  if (isDebugEnabled()) {
    growthbookContext.debug = true;
    growthbookContext.enableDevMode = true;
  }

  growthbookContext.trackingCallback = (experiment, result) => {
    if (!activationEvent) {
      return;
    }
    // call the activation event for the mParticle integration
    if (activationEvent.type === 'mparticle') {
      const eventAttributes = getMParticleEventAttributes(
        experiment.key,
        result.key,
        experiment.seed || '',
        attributes,
      );
      activationEvent.send(eventAttributes);
      if (growthbookContext.debug) {
        log(
          experiment.key,
          `${result.name} (key ${result.key})`,
          eventAttributes,
        );
      }
    }
  };

  const growthbook = new GrowthBook(growthbookContext);
  growthbook.setAttributes(attributes);

  if (
    Array.isArray(deferredTrackingCalls) &&
    deferredTrackingCalls.length > 0
  ) {
    growthbook.setDeferredTrackingCalls(deferredTrackingCalls);
    growthbook.fireDeferredTrackingCalls();
  }

  // give GrowthBook two seconds to initialise. In case of error it does not throw
  // but instead just sets all features etc to null
  // https://docs.growthbook.io/lib/js#error-handling
  await growthbook.init({timeout: 2000, streaming: enableStreaming});

  return growthbook;
}
